// W36: thin wrapper over the generated logs client.
// W27: admin-side query API over reach_logs. Read-only.
//
// Types come from `./generated/logs`; the request path stays on the house core.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';
import type * as gen from './generated/logs/index.js';

export type LogFamily = 'api' | 'query' | 'app' | 'lifecycle' | 'frontend';

export type LogStats = gen.LogStats;
export type LogQueryMeta = gen.LogQueryMeta;

/**
 * A log row. The generated `LogRow` carries the columns every family has; the
 * family-specific columns (`method`/`path`/`status_code`, `sql_redacted`,
 * `message`, `event_code`, …) arrive as extra properties, which the service
 * declares with `[JsonExtensionData]` and OpenAPI cannot name.
 */
export type LogRow = gen.LogRow & Record<string, unknown>;

/**
 * Query filters for `GET /v1/admin/logs`. W46: the handler binds every one of
 * these as a real minimal-API parameter (it used to read
 * `HttpContext.Request.Query` directly, so OpenAPI described the route with
 * zero parameters and neither SDK could type it). Keep in step with
 * `AllowedFamilies` / `AllowedLevels` in
 * `services/logs/Endpoints/AdminLogsQueryEndpoints.cs`.
 *
 * An unknown `family`, `level` or `status_class` is a 400 with
 * `ApiError.code` = `INVALID_FAMILY` / `INVALID_LEVEL` / `INVALID_STATUS_CLASS`;
 * a malformed `org_id`, `since` or `until` is rejected by the binder itself.
 */
export interface LogQueryOptions {
  family?: LogFamily;
  service?: string;
  env?: string;
  /**
   * Exact match on the stored level. Every writer is Serilog, so rows read
   * `Information` / `Warning` / `Error` / `Fatal` / `Verbose`; the allowlist is
   * case-insensitive and also accepts the Microsoft names (`Trace`,
   * `Critical`) and the short forms (`info`, `warn`).
   */
  level?: string;
  org_id?: string;
  correlation_id?: string;
  since?: string;
  until?: string;
  q?: string;
  /** `api` family only: ignored for the others. */
  status_class?: '2xx' | '3xx' | '4xx' | '5xx';
  /** 1…500, default 50. */
  limit?: number;
}

export interface LogQueryEnvelope extends Envelope<LogRow[]> {
  meta?: LogQueryMeta;
}

export function stats(): Promise<Envelope<LogStats>> {
  return http.get<Envelope<LogStats>>('/v1/admin/logs/stats');
}

export function query(opts: LogQueryOptions = {}): Promise<LogQueryEnvelope> {
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  return http.get<LogQueryEnvelope>('/v1/admin/logs', Object.keys(q).length ? { query: q } : undefined);
}

// ── Server-side API aggregation (7 September 2026) ─────────────────────────
//
// The admin console's API monitoring page used to compute every figure in the
// browser from a 500-row sample of `query({ family: 'api' })`. Its percentiles
// were percentiles of the sample, its buckets spanned the sample rather than
// the window, and past 500 rows every number on it was wrong. `apiSummary()`
// is the same page's numbers computed where the rows are.

/** Latency over the whole window, from `percentile_disc` and not from a sample. */
export interface ApiSummaryLatency {
  p50: number;
  p95: number;
  p99: number;
  max: number;
}

/**
 * One bucket of the series. Every bucket in the window is present, empty ones
 * included: a gap in traffic is a fact about the platform, and a chart that
 * closes the gap up reports an outage as a quiet patch.
 */
export interface ApiSummaryBucket {
  at: string;
  requests: number;
  errors: number;
  p95_ms: number;
}

/**
 * One endpoint, after server-side path templating: `/v1/contacts/{id}` rather
 * than one row per contact. The browser's `templatePath()` was the reference
 * and is no longer needed.
 */
export interface ApiSummaryEndpoint {
  method: string;
  path: string;
  service?: string | null;
  count: number;
  ok: number;
  client_error: number;
  server_error: number;
  avg_ms: number;
  p95_ms: number;
  max_ms: number;
  error_rate: number;
}

/**
 * Whether anything writes `api_logs` at all.
 *
 * An empty API monitoring page has two causes that look identical and are not
 * remotely the same: the platform served no requests in the window, or nothing
 * on the platform records requests. Today it is the second.
 * `UseRepletoryRequestLogging()` exists in `RepletoryLib.Logging.Database` and
 * is registered in no service, so the middleware that fills the table never
 * runs. The figures below are derived rather than declared: the logs service
 * knows which services ship it app and lifecycle lines and which have written
 * an API row, and a service that does one and not the other is a service whose
 * request logging is unwired. `instrumented` will start reading true on its own
 * the day the middleware is registered.
 */
export interface ApiSummaryInstrumentation {
  instrumented: boolean;
  services_shipping_logs: number;
  services_writing_api_logs: number;
  api_rows_total: number;
  api_latest_at?: string | null;
}

export interface ApiSummary {
  since: string;
  until: string;
  /** Window divided by the requested bucket count, never below one second. */
  bucket_seconds: number;
  total: number;
  ok: number;
  redirect: number;
  client_error: number;
  server_error: number;
  /** Percentage of requests answering 4xx or 5xx, to two decimals. */
  error_rate: number;
  latency: ApiSummaryLatency;
  series: ApiSummaryBucket[];
  /** The busiest 50 endpoints by request count. */
  endpoints: ApiSummaryEndpoint[];
  /** Distinct endpoints the window really holds, which `endpoints` is a slice of. */
  endpoint_total: number;
  instrumentation?: ApiSummaryInstrumentation | null;
}

export interface ApiSummaryOptions {
  /** ISO 8601. Required. */
  since: string;
  /** ISO 8601. Required, and after `since`. The window may span at most 92 days. */
  until: string;
  service?: string;
  status_class?: '2xx' | '3xx' | '4xx' | '5xx';
  /** An exact templated path, e.g. `/v1/contacts/{id}`. */
  path?: string;
  /** 1…200, default 60. */
  buckets?: number;
}

/**
 * Aggregate API request statistics over a window, computed in the database.
 *
 * Fails with 400 `INVALID_WINDOW` (missing, inverted or over 92 days) or
 * `INVALID_STATUS_CLASS`.
 */
export function apiSummary(opts: ApiSummaryOptions): Promise<Envelope<ApiSummary>> {
  const query: Record<string, string | number> = { since: opts.since, until: opts.until };
  if (opts.service) query.service = opts.service;
  if (opts.status_class) query.status_class = opts.status_class;
  if (opts.path) query.path = opts.path;
  if (opts.buckets !== undefined) query.buckets = opts.buckets;
  return http.get<Envelope<ApiSummary>>('/v1/admin/logs/api/summary', { query });
}
